/**
 * Notifications Service
 * Handles push notifications and in-app toasts
 * Enhanced with social, gamification, and proximity notifications
 */

import { icon } from '../utils/icons.js'
import { escapeHTML, escapeJSString } from '../utils/sanitize.js';
import { getErrorMessage } from '../utils/errorMessages.js';
import { getState, setState } from '../stores/state.js';
import { t } from '../i18n/index.js';

// Toast container reference
let toastContainer = null;

// Notification preferences storage key
const NOTIFICATION_PREFS_KEY = 'spothitch_notification_prefs';

// Default notification preferences
const DEFAULT_NOTIFICATION_PREFS = {
 enabled: true,
 // Social notifications
 newFriend: true,
 newMessage: true,
 friendNearby: true,
 // Gamification notifications
 badgeUnlocked: true,
 levelUp: true,
 dailyReward: true,
 // Spot notifications
 spotCheckin: true,
 spotRating: true,
 spotComment: true,
 // System notifications
 appUpdates: true,
 tips: true,
 // Timing preferences
 quietHoursEnabled: false,
 quietHoursStart: 22, // 10 PM
 quietHoursEnd: 8, // 8 AM
 // Nearby distance threshold (in km)
 nearbyDistance: 10,
};

// Scheduled notification IDs
const scheduledNotifications = new Map();


/**
 * Initialize notifications
 */
export async function initNotifications() {
 // Create toast container if it doesn't exist
 if (!toastContainer) {
 toastContainer = document.createElement('div');
 toastContainer.id = 'toast-container';
 toastContainer.className = 'fixed top-3 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-1.5 pointer-events-none max-w-[340px] w-[90%]';
 document.body.appendChild(toastContainer);
 }

 // Push permission is NOT requested here — user must opt-in explicitly
 // via the toggle in Profile settings or the smart prompt after Guardian/DM.
 // The foreground listener is managed exclusively by pushNotifications.js
 // to avoid duplicate registrations (see startForegroundListener).
 // Community SOS alerts are handled via the shared listener there.
}


/**
 * Show toast notification
 * @param {string} message - Message to display
 * @param {string} type - 'success' | 'error' | 'info' | 'warning'
 * @param {number} duration - Duration in ms
 */
export function showToast(message, type = 'info', duration = 4000) {
 if (!toastContainer) {
 toastContainer = document.createElement('div')
 toastContainer.id = 'toast-container'
 toastContainer.className = 'fixed top-3 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-1.5 pointer-events-none max-w-[340px] w-[90%]'
 document.body.appendChild(toastContainer)
 }

 const configs = {
 success: { icon: '✓', iconBg: '#22c55e', border: 'rgba(74,222,128,.4)', text: '#bbf7d0' },
 error: { icon: '✕', iconBg: '#ef4444', border: 'rgba(248,113,113,.4)', text: '#fecaca' },
 warning: { icon: '!', iconBg: '#f59e0b', border: 'rgba(251,191,36,.4)', text: '#fef3c7' },
 info: { icon: '↓', iconBg: '#2563eb', border: 'rgba(147,197,253,.4)', text: '#bfdbfe' },
 }
 const cfg = configs[type] || configs.info

 const toast = document.createElement('div')
 toast.className = `toast toast-${type}`
 toast.style.cssText = `
 display: inline-flex;
 align-items: center;
 gap: 7px;
 padding: 6px 13px 6px 7px;
 border-radius: 20px;
 border: 1px solid ${cfg.border};
 background: rgba(0,0,0,.45);
 backdrop-filter: blur(14px);
 -webkit-backdrop-filter: blur(14px);
 pointer-events: auto;
 animation: toastSlideDown 0.25s ease-out;
 white-space: nowrap;
 max-width: 100%;
 `

 toast.innerHTML = `
 <span class="rounded-full w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white shrink-0" style="background:${cfg.iconBg}">${cfg.icon}</span>
 <span class="text-xs font-medium overflow-hidden text-ellipsis" style="color:${cfg.text}">${escapeHTML(message)}</span>
 `

 toastContainer.appendChild(toast)

 if (!document.querySelector('#toast-animations')) {
 const style = document.createElement('style')
 style.id = 'toast-animations'
 style.textContent = `
 @keyframes toastSlideDown {
 from { opacity:0; transform:translateY(-10px); }
 to { opacity:1; transform:translateY(0); }
 }
 @keyframes toastFadeUp {
 to { opacity:0; transform:translateY(-8px); }
 }
 `
 document.head.appendChild(style)
 }

 setTimeout(() => {
 toast.style.animation = 'toastFadeUp 0.25s ease-out forwards'
 setTimeout(() => toast.remove(), 250)
 }, duration)
}

/**
 * Show success toast
 */
export function showSuccess(message, duration) {
 showToast(message, 'success', duration);
}

/**
 * Show error toast
 * @param {string} message - Error message or error code
 * @param {number} duration - Duration in ms
 */
export function showError(message, duration) {
 showToast(message, 'error', duration);
}

/**
 * Show friendly error toast based on error code
 * Uses humorous, user-friendly messages
 * @param {string} errorCode - Error code (e.g., 'NETWORK_OFFLINE', 'auth/user-not-found')
 * @param {number} duration - Duration in ms
 */
export function showFriendlyError(errorCode, duration = 5000) {
 const errorInfo = getErrorMessage(errorCode);
 const fullMessage = `${errorInfo.icon} ${errorInfo.message}`;
 showToast(fullMessage, errorInfo.type, duration);
}

/**
 * Show info toast
 */
export function showInfo(message, duration) {
 showToast(message, 'info', duration);
}

/**
 * Show warning toast
 */
export function showWarning(message, duration) {
 showToast(message, 'warning', duration);
}

/**
 * Announce message to screen readers
 * @param {string} message - Message to announce
 * @param {'polite' | 'assertive'} priority - Announcement priority
 */
export function announce(message, priority = 'polite') {
 const region = document.getElementById(`aria-live-${priority}`);
 if (region) {
 region.textContent = '';
 setTimeout(() => {
 region.textContent = message;
 }, 100);
 }
}

/**
 * Send local notification (when app is open)
 */
export function sendLocalNotification(title, body, data = {}) {
 if (!('Notification' in window)) {
 showToast(body, 'info')
 return
 }

 if (Notification.permission === 'granted') {
 // Use service worker registration for persistent notifications
 if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
 navigator.serviceWorker.ready.then((registration) => {
 registration.showNotification(title, {
 body,
 icon: '/icon-192.png',
 badge: '/icon-96.png',
 data,
 vibrate: data.type === 'guardian_overdue'
 ? [500, 200, 500, 200, 500, 200, 500]
 : [100, 50, 100],
 tag: data.tag || 'spothitch-notification',
 requireInteraction: data.requireInteraction || false,
 actions: data.type === 'guardian_overdue'
 ? [
 { action: 'checkin', title: t('imSafe') || "I'm safe" },
 { action: 'alert', title: t('sendAlert') || 'Send alert' },
 ]
 : [],
 })
 }).catch(() => {
 // Fallback to basic notification
 showBasicNotification(title, body, data)
 })
 } else {
 showBasicNotification(title, body, data)
 }
 } else {
 showToast(body, 'info')
 }
}

/**
 * Fallback notification without service worker
 */
function showBasicNotification(title, body, data = {}) {
 const notification = new Notification(title, {
 body,
 icon: '/icon-192.png',
 badge: '/icon-96.png',
 data,
 vibrate: [100, 50, 100],
 })

 notification.onclick = () => {
 window.focus()
 notification.close()
 if (data.url) {
 window.location.href = data.url
 }
 }
}

/**
 * Schedule a notification (if supported)
 */
export function scheduleNotification(title, body, triggerTime, data = {}) {
 const delay = triggerTime - Date.now();

 if (delay < 0) {
 console.warn('Notification time is in the past');
 return null;
 }

 const randomBytes = new Uint8Array(6)
 crypto.getRandomValues(randomBytes)
 const randomStr = Array.from(randomBytes, b => b.toString(36)).join('').slice(0, 8)
 const id = data.id || `notif_${Date.now()}_${randomStr}`
 const timeoutId = setTimeout(() => {
 scheduledNotifications.delete(id);
 sendLocalNotification(title, body, data);
 }, delay);

 scheduledNotifications.set(id, timeoutId);
 return id;
}

// ==================== SPOT NOTIFICATIONS ====================

// Store subscribed spots in localStorage
const SPOT_SUBSCRIPTIONS_KEY = 'spothitch_spot_subscriptions';

/**
 * Get notification settings for spots
 */
export function getSpotNotificationSettings() {
 try {
 const saved = localStorage.getItem(SPOT_SUBSCRIPTIONS_KEY);
 return saved ? JSON.parse(saved) : { enabled: true, spots: [], types: ['checkin', 'rating', 'comment'] };
 } catch {
 return { enabled: true, spots: [], types: ['checkin', 'rating', 'comment'] };
 }
}

/**
 * Save notification settings for spots
 */
function saveSpotNotificationSettings(settings) {
 try {
 localStorage.setItem(SPOT_SUBSCRIPTIONS_KEY, JSON.stringify(settings));
 } catch (e) {
 console.warn('Failed to save spot notification settings:', e);
 }
}

/**
 * Subscribe to notifications for a spot you created
 * @param {number|string} spotId - The spot ID
 * @param {string} spotName - The spot name for display
 */
export function subscribeToSpotNotifications(spotId, spotName) {
 const settings = getSpotNotificationSettings();

 if (!settings.spots.find(s => s.id === spotId)) {
 settings.spots.push({
 id: spotId,
 name: spotName,
 subscribedAt: new Date().toISOString()
 });
 saveSpotNotificationSettings(settings);
 showToast((t('notifSpotSubscribed') || 'Notifications activées pour "{name}"').replace('{name}', spotName), 'success');
 }

 return true;
}

/**
 * Unsubscribe from notifications for a spot
 * @param {number|string} spotId - The spot ID
 */
export function unsubscribeFromSpotNotifications(spotId) {
 const settings = getSpotNotificationSettings();
 settings.spots = settings.spots.filter(s => s.id !== spotId);
 saveSpotNotificationSettings(settings);
 showToast(t('notifSpotUnsubscribed') || 'Notifications désactivées pour ce spot', 'info');
}

/**
 * Check if subscribed to a spot's notifications
 * @param {number|string} spotId - The spot ID
 */
export function isSubscribedToSpot(spotId) {
 const settings = getSpotNotificationSettings();
 return settings.spots.some(s => s.id === spotId);
}

/**
 * Toggle spot notification subscription
 * @param {number|string} spotId - The spot ID
 * @param {string} spotName - The spot name for display
 */
export function toggleSpotNotifications(spotId, spotName) {
 if (isSubscribedToSpot(spotId)) {
 unsubscribeFromSpotNotifications(spotId);
 return false;
 } else {
 subscribeToSpotNotifications(spotId, spotName);
 return true;
 }
}

/**
 * Notify about activity on a spot
 * This is called when someone interacts with a spot you're subscribed to
 * @param {string} type - 'checkin' | 'rating' | 'comment'
 * @param {object} data - Activity data
 */
export function notifySpotActivity(type, data) {
 const settings = getSpotNotificationSettings();

 // Check if notifications are enabled and spot is subscribed
 if (!settings.enabled) return;
 if (!settings.types.includes(type)) return;

 const isSubscribed = settings.spots.some(s => s.id === data.spotId);
 if (!isSubscribed) return;

 const traveler = data.userName || (t('notifATraveler') || 'Un voyageur')
 const messages = {
 checkin: {
 title: t('notifSpotCheckinTitle') || 'Nouveau check-in !',
 body: (t('notifSpotCheckinBody') || '{user} a validé ton spot "{spot}"').replace('{user}', traveler).replace('{spot}', data.spotName),
 },
 rating: {
 title: (t('notifSpotRatingTitle') || 'Nouvelle évaluation !').replace('{rating}', data.rating || ''),
 body: (t('notifSpotRatingBody') || '{user} a noté ton spot "{spot}" : {rating}/5').replace('{user}', traveler).replace('{spot}', data.spotName).replace('{rating}', data.rating || ''),
 },
 comment: {
 title: t('notifSpotCommentTitle') || 'Nouveau commentaire !',
 body: (t('notifSpotCommentBody') || '{user} a commenté ton spot "{spot}"').replace('{user}', traveler).replace('{spot}', data.spotName),
 },
 };

 const msg = messages[type];
 if (msg) {
 sendLocalNotification(msg.title, msg.body, {
 type: 'spot_activity',
 spotId: data.spotId,
 activityType: type,
 url: `/?spot=${data.spotId}`
 });

 // Also show in-app toast
 showToast(msg.body, 'info', 5000);
 }
}

/**
 * Get all subscribed spots
 */
export function getSubscribedSpots() {
 const settings = getSpotNotificationSettings();
 return settings.spots;
}

/**
 * Set notification types to receive
 * @param {string[]} types - Array of types: 'checkin', 'rating', 'comment'
 */
export function setSpotNotificationTypes(types) {
 const settings = getSpotNotificationSettings();
 settings.types = types;
 saveSpotNotificationSettings(settings);
}

/**
 * Toggle all spot notifications
 * @param {boolean} enabled - Enable or disable
 */
export function setSpotNotificationsEnabled(enabled) {
 const settings = getSpotNotificationSettings();
 settings.enabled = enabled;
 saveSpotNotificationSettings(settings);
 showToast(enabled ? (t('notifSpotEnabled') || 'Notifications de spots activées') : (t('notifSpotDisabled') || 'Notifications de spots désactivées'), 'info');
}

// ==================== NOTIFICATION PREFERENCES ====================

/**
 * Get notification preferences
 * @returns {Object} Notification preferences
 */
export function getNotificationPreferences() {
 try {
 const saved = localStorage.getItem(NOTIFICATION_PREFS_KEY);
 return saved ? { ...DEFAULT_NOTIFICATION_PREFS, ...JSON.parse(saved) } : { ...DEFAULT_NOTIFICATION_PREFS };
 } catch {
 return { ...DEFAULT_NOTIFICATION_PREFS };
 }
}

/**
 * Save notification preferences
 * @param {Object} prefs - Preferences to save
 */
export function saveNotificationPreferences(prefs) {
 try {
 const current = getNotificationPreferences();
 const updated = { ...current, ...prefs };
 localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(updated));
 return updated;
 } catch (e) {
 console.warn('Failed to save notification preferences:', e);
 return getNotificationPreferences();
 }
}

/**
 * Check if a notification type is enabled
 * @param {string} type - Notification type
 * @returns {boolean} Whether the notification is enabled
 */
export function isNotificationEnabled(type) {
 const prefs = getNotificationPreferences();
 if (!prefs.enabled) return false;

 // Check quiet hours
 if (prefs.quietHoursEnabled) {
 const hour = new Date().getHours();
 const start = prefs.quietHoursStart;
 const end = prefs.quietHoursEnd;

 // Handle overnight quiet hours (e.g., 22:00 - 08:00)
 if (start > end) {
 if (hour >= start || hour < end) return false;
 } else {
 if (hour >= start && hour < end) return false;
 }
 }

 return prefs[type] !== false;
}

/**
 * Toggle a notification preference
 * @param {string} type - Notification type
 * @returns {boolean} New value
 */
export function toggleNotificationPreference(type) {
 const prefs = getNotificationPreferences();
 const newValue = !prefs[type];
 saveNotificationPreferences({ [type]: newValue });
 return newValue;
}

// ==================== SOCIAL NOTIFICATIONS ====================

/**
 * Notify when someone becomes a friend
 * @param {Object} friend - Friend data
 */
export function notifyNewFriend(friend) {
 if (!isNotificationEnabled('newFriend')) return;

 const title = t('notifNewFriendTitle') || 'Nouvel ami !';
 const body = (t('notifNewFriendBody') || '{name} a accepte ta demande d\'ami').replace('{name}', friend.name || (t('notifATraveler') || 'Un voyageur'));
 const friendAvatar = friend.avatar || '';

 sendLocalNotification(title, body, {
 type: 'new_friend',
 friendId: friend.id,
 url: '/?tab=social',
 });

 // Show in-app toast
 showToast(`${friendAvatar} ${body}`.trim(), 'success', 5000);

 // Announce for screen readers
 announce((t('notifNewFriendAnnounce') || 'Nouvel ami: {name}').replace('{name}', friend.name || (t('notifATraveler') || 'Un voyageur')));
}

/**
 * Notify when receiving a new message
 * @param {Object} message - Message data
 */
export function notifyNewMessage(message) {
 if (!isNotificationEnabled('newMessage')) return;

 const senderName = message.senderName || (t('notifATraveler') || 'Un voyageur');
 const title = (t('notifNewMessageTitle') || 'Message de {name}').replace('{name}', senderName);
 const body = message.preview || (message.text ? message.text.substring(0, 50) + '...' : null) || (t('notifNewMessageBody') || 'Nouveau message');

 sendLocalNotification(title, body, {
 type: 'new_message',
 senderId: message.senderId,
 messageId: message.id,
 url: `/?tab=chat&friend=${message.senderId}`,
 });

 // Show in-app toast
 showToast(`${message.senderAvatar || ''} ${senderName}: "${body}"`, 'info', 4000);

 // Update unread count in state
 const state = getState();
 setState({
 unreadFriendMessages: (state.unreadFriendMessages || 0) + 1,
 });
}

/**
 * Notify when a friend is nearby
 * @param {Object} friend - Friend data with location
 * @param {number} distance - Distance in km
 */
export function notifyFriendNearby(friend, distance) {
 if (!isNotificationEnabled('friendNearby')) return;

 const prefs = getNotificationPreferences();
 if (distance > prefs.nearbyDistance) return;

 // Avoid duplicate notifications for the same friend
 const notifyKey = `nearby_${friend.id}`;
 const lastNotified = sessionStorage.getItem(notifyKey);
 const now = Date.now();

 // Only notify once per hour per friend
 if (lastNotified && now - parseInt(lastNotified) < 3600000) return;
 sessionStorage.setItem(notifyKey, now.toString());

 const title = t('notifFriendNearbyTitle') || 'Ami a proximite !';
 const distanceText = distance < 1
 ? (t('notifDistanceMeters') || 'a {n}m').replace('{n}', Math.round(distance * 1000))
 : (t('notifDistanceKm') || 'a {n}km').replace('{n}', distance.toFixed(1));
 const body = (t('notifFriendNearbyBody') || '{name} est {distance} de toi').replace('{name}', friend.name || (t('notifAFriend') || 'Un ami')).replace('{distance}', distanceText);

 sendLocalNotification(title, body, {
 type: 'friend_nearby',
 friendId: friend.id,
 distance,
 url: '/?tab=social&nearby=true',
 });

 // Show prominent in-app notification
 showToast(`${friend.avatar || ''} ${body}`, 'info', 8000);

 // Play sound if available
 if (window.playSound) {
 window.playSound('notification');
 }
}

// ==================== GAMIFICATION NOTIFICATIONS ====================

/**
 * Notify when a badge is unlocked
 * @param {Object} badge - Badge data
 */
export function notifyBadgeUnlocked(badge) {
 if (!isNotificationEnabled('badgeUnlocked')) return;

 const title = t('notifBadgeUnlockedTitle') || 'Badge debloque !';
 const body = (t('notifBadgeUnlockedBody') || 'Tu as obtenu le badge "{name}"').replace('{name}', badge.name);

 sendLocalNotification(title, body, {
 type: 'badge_unlocked',
 badgeId: badge.id,
 url: '/?tab=profile&badges=true',
 });

 // In-app notification is handled by gamification service
 // but we announce for accessibility
 announce((t('notifBadgeUnlockedAnnounce') || 'Badge debloque: {name}').replace('{name}', badge.name));
}

/**
 * Notify when leveling up
 * @param {number} newLevel - New level reached
 * @param {Object} rewards - Optional rewards for leveling up
 */
export function notifyLevelUp(newLevel, rewards = {}) {
 if (!isNotificationEnabled('levelUp')) return;

 const title = (t('notifLevelUpTitle') || 'Niveau {level} atteint !').replace('{level}', newLevel);
 let body = t('notifLevelUpBody') || 'Felicitations ! Continue comme ca !';

 if (rewards.points) {
 body += ` ${(t('notifLevelUpBonus') || '+{n} pts bonus !').replace('{n}', rewards.points)}`;
 }
 if (rewards.title) {
 body = (t('notifLevelUpTitle2') || 'Nouveau titre: {name} !').replace('{name}', rewards.title.name);
 }

 sendLocalNotification(title, body, {
 type: 'level_up',
 level: newLevel,
 rewards,
 url: '/?tab=profile',
 });

 // Announce for accessibility
 announce((t('notifLevelUpAnnounce') || 'Niveau {level} atteint').replace('{level}', newLevel));
}


// ==================== DAILY REWARD NOTIFICATION ====================

/**
 * Notify about daily reward available
 */
export function notifyDailyRewardAvailable() {
 if (!isNotificationEnabled('dailyReward')) return;

 const state = getState();
 const lastClaim = state.lastDailyRewardClaim;
 const today = new Date().toDateString();

 if (lastClaim === today) return; // Already claimed today

 const title = t('notifDailyRewardTitle') || 'Recompense quotidienne !';
 const body = t('notifDailyRewardBody') || 'Ta recompense quotidienne t\'attend ! Connecte-toi pour la recuperer.';

 sendLocalNotification(title, body, {
 type: 'daily_reward',
 url: '/',
 });

 showToast(t('notifDailyRewardToast') || 'Ta recompense quotidienne est disponible !', 'info', 5000);
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Calculate distance between two points in km (Haversine formula)
 * @param {number} lat1 - Latitude 1
 * @param {number} lon1 - Longitude 1
 * @param {number} lat2 - Latitude 2
 * @param {number} lon2 - Longitude 2
 * @returns {number} Distance in km
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
 const R = 6371; // Earth's radius in km
 const dLat = (lat2 - lat1) * Math.PI / 180;
 const dLon = (lon2 - lon1) * Math.PI / 180;
 const a =
 Math.sin(dLat / 2) * Math.sin(dLat / 2) +
 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
 Math.sin(dLon / 2) * Math.sin(dLon / 2);
 const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
 return R * c;
}

/**
 * Check friends proximity and notify
 * @param {Array} friends - Array of friends with locations
 * @param {Object} userLocation - User's current location
 */
export function checkFriendsProximity(friends, userLocation) {
 if (!friends || !userLocation) return;

 const prefs = getNotificationPreferences();

 friends.forEach(friend => {
 if (friend.location) {
 const distance = calculateDistance(
 userLocation.lat,
 userLocation.lng,
 friend.location.lat,
 friend.location.lng
 );

 if (distance <= prefs.nearbyDistance) {
 notifyFriendNearby(friend, distance);
 }
 }
 });
}

/**
 * Cancel a scheduled notification
 * @param {string} id - Notification ID
 */
export function cancelScheduledNotification(id) {
 const timeoutId = scheduledNotifications.get(id);
 if (timeoutId) {
 clearTimeout(timeoutId);
 scheduledNotifications.delete(id);
 }
}

/**
 * Cancel all scheduled notifications
 */
export function cancelAllScheduledNotifications() {
 scheduledNotifications.forEach((timeoutId) => {
 clearTimeout(timeoutId);
 });
 scheduledNotifications.clear();
}

export default {
 initNotifications,
 showToast,
 showSuccess,
 showError,
 showFriendlyError,
 showInfo,
 showWarning,
 announce,
 sendLocalNotification,
 scheduleNotification,
 // Spot notifications
 getSpotNotificationSettings,
 subscribeToSpotNotifications,
 unsubscribeFromSpotNotifications,
 isSubscribedToSpot,
 toggleSpotNotifications,
 notifySpotActivity,
 getSubscribedSpots,
 setSpotNotificationTypes,
 setSpotNotificationsEnabled,
 // Notification preferences
 getNotificationPreferences,
 saveNotificationPreferences,
 isNotificationEnabled,
 toggleNotificationPreference,
 // Social notifications
 notifyNewFriend,
 notifyNewMessage,
 notifyFriendNearby,
 // Gamification notifications
 notifyBadgeUnlocked,
 notifyLevelUp,
 notifyDailyRewardAvailable,
 // Helper functions
 calculateDistance,
 checkFriendsProximity,
 cancelScheduledNotification,
 cancelAllScheduledNotifications,
};

// ─── Community SOS Alert Banner ──────────────────────────────────────────────

/**
 * Show a persistent red banner when a community SOS alert arrives.
 * The banner shows the distance and has buttons to view on map or call emergency.
 */
// Exposed globally for the foreground listener in pushNotifications.js
window._showCommunitySOSBanner = _showCommunitySOSBanner
function _showCommunitySOSBanner(data) {
 if (!data) return
 const { voyagerName, lat, lng, distance, alertType } = data
 const existing = document.getElementById('community-sos-banner')
 if (existing) existing.remove()

 const name = voyagerName ? escapeHTML(voyagerName) : (t('aHitchhiker') || 'Un autostoppeur')
 const distText = parseFloat(distance) < 1
 ? (t('veryClose') || 'tout près')
 : `${distance}km`

 const banner = document.createElement('div')
 banner.id = 'community-sos-banner'
 banner.setAttribute('role', 'alert')
 banner.setAttribute('aria-live', 'assertive')
 banner.className = 'fixed top-0 left-0 right-0 z-[10000] bg-gradient-to-br from-red-600 to-red-800 text-white p-4 text-center shadow-[0_4px_20px_rgba(220,38,38,.5)] animate-[slideDown_.3s_ease-out]'
 banner.innerHTML = `
 <style>@keyframes slideDown{from{transform:translateY(-100%)}to{transform:translateY(0)}}</style>
 <div class="max-w-[400px] mx-auto">
 <div class="font-extrabold text-[15px] mb-1">
 ${icon(alertType === 'silent' ? 'volume-x' : 'siren', 'w-4 h-4 inline mr-1')} SOS ${distText}
 </div>
 <div class="text-[13px] opacity-90 mb-3">
 ${name} ${t('needsHelpNearYou') || 'a besoin d\'aide près de vous'}
 </div>
 <div class="flex gap-2 justify-center">
 <button onclick="showCommunitySOSOnMap(${Number(lat)||0},${Number(lng)||0},'${escapeJSString(voyagerName || '')}')" class="flex-1 p-2.5 rounded-xl border-none bg-white text-red-600 font-bold text-[13px] cursor-pointer">${t('seeOnMap') || 'Voir sur la carte'}</button>
 <a href="tel:112" class="py-2.5 px-4 rounded-xl border-2 border-white/30 bg-transparent text-white font-bold text-[13px] no-underline flex items-center gap-1">${icon('phone', 'w-4 h-4 inline mr-1')} 112</a>
 <button onclick="document.getElementById('community-sos-banner')?.remove()" class="p-2.5 rounded-xl border-2 border-white/30 bg-transparent text-white text-[13px] cursor-pointer" aria-label="${t('close') || 'Fermer'}">${icon('x', 'w-4 h-4')}</button></div></div>
 `
 document.body.appendChild(banner)

 // Auto-dismiss after 60 seconds
 setTimeout(() => banner.remove(), 60000)
}

// Handler to show community SOS on the map
window.showCommunitySOSOnMap = (lat, lng, name) => {
 document.getElementById('community-sos-banner')?.remove()
 // Switch to map tab
 window.setState?.({ activeTab: 'map' })
 // Fly to SOS position
 setTimeout(() => {
 const map = window.__mapInstance
 if (map) {
 map.flyTo({ center: [lng, lat], zoom: 15, duration: 1500 })
 // Add a temporary pulsing marker
 _addSOSMarker(map, lat, lng, name)
 }
 }, 300)
}

/**
 * Add a pulsing red SOS marker on the map.
 * Auto-removed after 5 minutes.
 */
function _addSOSMarker(map, lat, lng, name) {
 // Create pulsing dot element
 const el = document.createElement('div')
 el.className = 'w-6 h-6 rounded-full bg-red-600 border-[3px] border-white animate-[sosPulse_1.5s_ease-out_infinite]'
 // Inject keyframes if not already present
 if (!document.getElementById('sos-pulse-style')) {
 const style = document.createElement('style')
 style.id = 'sos-pulse-style'
 style.textContent = `
 @keyframes sosPulse {
 0% { box-shadow: 0 0 0 0 rgba(220,38,38,.7); }
 70% { box-shadow: 0 0 0 20px rgba(220,38,38,0); }
 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
 }
 `
 document.head.appendChild(style)
 }

 // Use maplibregl Marker
 try {
 const maplibregl = window.maplibregl || window.mapboxgl
 if (maplibregl) {
 const popup = new maplibregl.Popup({ offset: 25, closeOnClick: false })
 .setHTML(`<div class="p-2 text-center text-[13px]">
 <strong class="text-red-600">SOS</strong><br/>
 ${name ? escapeHTML(name) : 'Voyageur en détresse'}<br/>
 <a href="tel:112" class="text-red-600 font-bold">Appeler 112</a></div>`)

 const marker = new maplibregl.Marker({ element: el })
 .setLngLat([lng, lat])
 .setPopup(popup)
 .addTo(map)

 popup.addTo(map)

 // Auto-remove after 5 minutes
 setTimeout(() => {
 marker.remove()
 popup.remove()
 }, 5 * 60 * 1000)
 }
 } catch (err) {
 console.warn('[CommunityAlert] Failed to add map marker:', err.message)
 }
}
